import type { MakeADT, MakeADTMember } from 'ts-adt/MakeADT'
import { makeMatchI, makeMatchPI } from 'ts-adt/MakeADT'

import type { DTAggregate, DTCheckbox, DTDate, DTFile, DTFormula, DTId, DTNumber, DTPerson, DTSelect, TypeInstanceMap } from './data'
import type { RichTextContentProtocol } from './richtext'
import type { ViewField } from './view'

export type FieldId = string

export enum DateFieldUnit {
    'YEAR' = 'YEAR',
    'MINUTE' = 'MINUTE',
    'MONTH' = 'MONTH',
    'DAY' = 'DAY',
    'DAY_MINUTE' = 'DAY_MINUTE'
}

export const fieldTypes = [
    'id',
    'number',
    'text',
    'url',
    'phoneNumber',
    'email',
    'file',
    'video',
    'select',
    'date',
    'person',
    'formula',
    'notes',
    'role',
    'checkbox',
    'department',
    'aggregation',
    'singleSelect',
    'parentDepartment',

    // 入参类型，没有具体业务类型，不是真正的field
    'runtime'
] as const

export type FieldType = typeof fieldTypes[number]

export interface FieldProtocol {
    id: string
    name: string
    type: FieldType
    dsId: string
    dsName?: string
    innerType: TypeInstanceMap

    // 连接表中字段
    join?: {
        joinDsId: string
        joinFieldId: string
    }
}

export type IdField = FieldProtocol & {
    idConfig: DTId
}

export type NumberField = FieldProtocol & {
    number: DTNumber
}

export type DateField = FieldProtocol & {
    date: DTDate
}

export type SelectField = FieldProtocol & {
    select: DTSelect
}

export type PersonField = FieldProtocol & {
    person: DTPerson
}

// export type DepartmentField = FieldProtocol & {
//     de: DTPerson
// }

export type FormulaField = FieldProtocol & {
    formula: DTFormula
}

export type CheckboxField = FieldProtocol & {
    checkbox: DTCheckbox
}

export type AggregateField = FieldProtocol & {
    aggregation?: DTAggregate
}

export type SingleSelectField = FieldProtocol & {
    singleSelect: DTSelect
}

export type Field = MakeADT<
    'type',
    {
        id: IdField
        text: FieldProtocol
        url: FieldProtocol
        phoneNumber: FieldProtocol
        email: FieldProtocol
        file: FieldProtocol
        video: FieldProtocol
        number: NumberField
        select: SelectField
        date: DateField
        person: PersonField
        formula: FormulaField
        notes: FieldProtocol
        role: FieldProtocol
        checkbox: CheckboxField
        department: FieldProtocol
        aggregation: AggregateField
        singleSelect: SingleSelectField
        parentDepartment: FieldProtocol
        recordId: FieldProtocol

        // 入参类型，没有具体业务类型，不是真正的field
        runtime: FieldProtocol
    }
>

export type FieldMember<Type extends Field['type']> = MakeADTMember<'type', Field, Type>
export type ViewFieldMember<Type extends ViewField['type']> = MakeADTMember<'type', ViewField, Type>
export const matchFieldPI = makeMatchPI<'type'>('type')
export const matchFieldI = makeMatchI<'type'>('type')

export type FormulaSelectValue = {
    label: string
    value: string
}[]

export type SchemaProtocol = {
    schema: {
        [key: string]: Field
    }
}
export type StringArrayValue = string[]

export type TextValue = string

export type RichTextValue = RichTextContentProtocol

export type NumberValue = number | string

export type DateValue = number | string

export type FileValue = DTFile[]

export type BoolValue = boolean

export type NullValue = null

export type BaseCellValue = TextValue | NumberValue | BoolValue | DateValue | StringArrayValue | RichTextValue

export type FormulaValue = BaseCellValue | undefined

export type AggregationValue =
    | TextValue
    | NumberValue
    | BoolValue
    | DateValue
    | StringArrayValue
    | TextValue[]
    | NumberValue[]
    | BoolValue[]
    | DateValue[]
    | undefined

export type FieldCellValue = RichTextValue | FormulaValue

export type JoinValue = FieldCellValue

// export interface PersonItemValue {
//     avatar: string | undefined
//     userId: string
//     username: string
// }

export type NullFieldValue = {
    id: string
    name: string
    type: 'null'
    dsId: string
    value: NullValue
    innerType: TypeInstanceMap
    join?: {
        joinDsId: string
        joinFieldId: string
    }
}

export type FieldValue = {
    value?: FieldCellValue
    // typeInstance?: TypeInstanceMap
}

export type FieldADTValue = MakeADT<
    'type',
    {
        id: FieldProtocol & { value: TextValue }
        text: FieldProtocol & { value: TextValue }
        url: FieldProtocol & { value: TextValue }
        phoneNumber: FieldProtocol & { value: TextValue }
        email: FieldProtocol & { value: TextValue }
        file: FieldProtocol & { value: FileValue }
        video: FieldProtocol & { value: FileValue }
        person: PersonField & { value: StringArrayValue }
        number: NumberField & { value: NumberValue }
        select: SelectField & { value: StringArrayValue }
        date: DateField & { value: DateValue }
        formula: FormulaField & { value: FormulaValue }
        notes: FieldProtocol & { value: RichTextValue }
        role: FieldProtocol & { value: StringArrayValue }
        checkbox: CheckboxField & { value: BoolValue }
        department: FieldProtocol & { value: StringArrayValue }
        aggregation: AggregateField & { value: AggregationValue }
        singleSelect: SingleSelectField & { value: StringArrayValue }
        parentDepartment: FieldProtocol & { value: StringArrayValue }
        recordId: FieldProtocol & { value: TextValue }
    }
>

export type FilterFieldADTValue = (NullFieldValue | FieldADTValue) & { isIgnore?: boolean }

/** 变量选择器Field */
export type VariableFieldADTValue = MakeADT<
    'type',
    {
        id: { value: TextValue }
        text: { value: TextValue }
        url: { value: TextValue }
        phoneNumber: { value: TextValue }
        email: { value: TextValue }
        file: { value: FileValue }
        video: { value: FileValue }
        formula: { value: FormulaValue }
        person: { value: StringArrayValue }
        number: { value: NumberValue }
        notes: { value: RichTextValue }
        select: { value: StringArrayValue }
        date: { value: DateValue; format?: string }
        role: { value: StringArrayValue }
        checkbox: { value: BoolValue }
        department: { value: StringArrayValue }
        aggregation: { value: AggregationValue }
        singleSelect: { value: StringArrayValue }
        parentDepartment: { value: StringArrayValue }
        recordId: { value: TextValue }
    }
>

export type TableCellProtocol = FieldADTValue & {
    appId: string
    recordId: string
}

export type FieldIdWithValue = { fieldId: string; value: FieldCellValue | undefined }

export type InnerTypeADTValue = MakeADT<
    'innerType',
    {
        TEXT: { value: TextValue }
        NUMBER: { value: NumberValue }
        BOOL: { value: BoolValue }
        DATE: { value: DateValue }
        ARRAY: { value: StringArrayValue }
        // RICH_TEXT: { value: RichTextValue }
        NULL: { value: NullValue }
    }
>
